import React, { useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { 
  Box, 
  Tabs, 
  Tab, 
  Typography, 
  Stack, 
  Pagination, 
  CircularProgress,
  Grid,
  FormControlLabel,
  Switch,
  useTheme
} from '@mui/material'
import { styled } from '@mui/material/styles'
import WorkIcon from '@mui/icons-material/Work'
import BusinessIcon from '@mui/icons-material/Business'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import FavoriteIcon from '@mui/icons-material/Favorite'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import { ListItemJob } from '../../../components/Listing/ListItemJob'
import JobModal from './JobModal'
import HospitalCard from './HospitalCard'
import AIChatBot from './AIChatBot'

const JOBS_PER_PAGE = 10

const StyledSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase': {
    color: theme.palette.grey[400],
    '&.Mui-checked': {
      color: theme.palette.error.main,
    },
    '&.Mui-checked + .MuiSwitch-track': {
      backgroundColor: theme.palette.error.main,
    },
  },
  '& .MuiSwitch-track': {
    backgroundColor: theme.palette.grey[300],
  },
}))

const AIJobInterface = ({ onFavoriteClick, favoritesOnly, toggleFavoritesFilter, signedIn, employerUserId }) => {
  const theme = useTheme()
  const [activeTab, setActiveTab] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedJob, setSelectedJob] = useState(null)

  // Get AI job search state from Redux
  const { 
    exactMatches, 
    fuzzyMatches, 
    fuzzyMatchesLoading,
    hospitalMatches,
    loading,
    isAiTyping
  } = useSelector(state => state.aiJobSearch)

  // Filter jobs based on favorites
  const filterJobsByFavorites = (jobs) => {
    if (!favoritesOnly) return jobs
    return jobs.filter(job => job.candidateJob && job.candidateJob.favorite)
  }

  // Pagination calculations
  const getCurrentJobs = () => {
    if (activeTab === 0 && exactMatches?.jobs) {
      const filteredJobs = filterJobsByFavorites(exactMatches.jobs)
      const startIndex = (currentPage - 1) * JOBS_PER_PAGE
      const endIndex = startIndex + JOBS_PER_PAGE
      return filteredJobs.slice(startIndex, endIndex)
    }
    if (activeTab === 1 && fuzzyMatches?.jobs) {
      const filteredJobs = filterJobsByFavorites(fuzzyMatches.jobs)
      const startIndex = (currentPage - 1) * JOBS_PER_PAGE
      const endIndex = startIndex + JOBS_PER_PAGE
      return filteredJobs.slice(startIndex, endIndex)
    }
    return []
  }

  const getTotalPages = () => {
    if (activeTab === 0 && exactMatches?.jobs) {
      const filteredJobs = filterJobsByFavorites(exactMatches.jobs)
      return Math.ceil(filteredJobs.length / JOBS_PER_PAGE)
    }
    if (activeTab === 1 && fuzzyMatches?.jobs) {
      const filteredJobs = filterJobsByFavorites(fuzzyMatches.jobs)
      return Math.ceil(filteredJobs.length / JOBS_PER_PAGE)
    }
    return 1
  }

  const currentAIJobs = getCurrentJobs()
  const totalAIPages = getTotalPages()

  // Reset page when favorites filter changes
  React.useEffect(() => {
    setCurrentPage(1)
  }, [favoritesOnly])

  // Event handlers
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
    setCurrentPage(1) // Reset to first page when switching tabs
  }

  const handleJobClick = useCallback(job => {
    setSelectedJob(job)
  }, [])

  const handleJobClickById = useCallback((jobId) => {
    // Find the job in the current matches and call handleJobClick with the full job object
    const allJobs = [
      ...(exactMatches?.jobs || []),
      ...(fuzzyMatches?.jobs || [])
    ]
    const job = allJobs.find(j => j.id === jobId)
    if (job) {
      handleJobClick(job)
    }
  }, [exactMatches?.jobs, fuzzyMatches?.jobs, handleJobClick])

  const handleCloseModal = useCallback(() => {
    setSelectedJob(null)
  }, [])

  const handlePageChange = useCallback((event, value) => {
    setCurrentPage(value)
  }, [])

  return (
    <>
      <Grid 
        container 
        className="ai-job-results-container"
        sx={{ 
          height: '100%', 
          maxHeight: 'calc(100vh - 64px)',
          marginRight: 0,
          transition: 'margin-right 0.3s ease',
        }}
      >
        {/* Main Panel - AI Results */}
        <Grid
          item
          xs={12}
          md={12}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 2,
              borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
              backgroundColor: 'white',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography variant='h6' sx={{ color: theme.palette.secondary.main, fontWeight: 'bold' }}>
                AI Job Search Results
              </Typography>
            </Box>
{signedIn && !employerUserId && (
              <FormControlLabel
                control={
                  <StyledSwitch
                    checked={favoritesOnly}
                    onChange={toggleFavoritesFilter}
                    icon={<FavoriteBorderIcon />}
                    checkedIcon={<FavoriteIcon />}
                  />
                }
                label='Favorites Only'
                labelPlacement='start'
                sx={{ marginRight: 0 }}
              />
            )}
          </Box>

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant='fullWidth'
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              backgroundColor: isAiTyping ? `${theme.palette.secondary.main}14` : theme.palette.grey[100],
              transition: 'background-color 0.3s ease',
              '& .MuiTab-root': {
                fontWeight: 'medium',
                textTransform: 'none',
                fontSize: '0.9rem',
                '&.Mui-selected': {
                  color: theme.palette.secondary.main,
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: theme.palette.secondary.main,
                animation: isAiTyping ? 'searchPulse 2s ease-in-out infinite' : 'none',
                '@keyframes searchPulse': {
                  '0%': { 
                    backgroundColor: theme.palette.secondary.main,
                    boxShadow: `0 0 0 0 ${theme.palette.secondary.main}66`
                  },
                  '50%': { 
                    backgroundColor: theme.palette.info.main,
                    boxShadow: `0 0 0 8px ${theme.palette.secondary.main}00`
                  },
                  '100%': { 
                    backgroundColor: theme.palette.secondary.main,
                    boxShadow: `0 0 0 0 ${theme.palette.secondary.main}00`
                  },
                },
              },
            }}
          >
            <Tab
              icon={<WorkIcon />}
              label={
                <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                  {`Exact Matches ${exactMatches?.jobs?.length ? `(${filterJobsByFavorites(exactMatches.jobs).length})` : ''}`}
                  {isAiTyping && (
                    <Box component="span" sx={{ ml: 1, display: 'flex' }}>
                      <Box
                        component="span"
                        sx={{
                          animation: 'typing 1.5s infinite',
                          animationDelay: '0s',
                          opacity: 0,
                          '@keyframes typing': {
                            '0%': { opacity: 0 },
                            '33%': { opacity: 1 },
                            '66%': { opacity: 1 },
                            '100%': { opacity: 0 },
                          },
                        }}
                      >
                        •
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          animation: 'typing 1.5s infinite',
                          animationDelay: '0.5s',
                          opacity: 0,
                          '@keyframes typing': {
                            '0%': { opacity: 0 },
                            '33%': { opacity: 1 },
                            '66%': { opacity: 1 },
                            '100%': { opacity: 0 },
                          },
                        }}
                      >
                        •
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          animation: 'typing 1.5s infinite',
                          animationDelay: '1s',
                          opacity: 0,
                          '@keyframes typing': {
                            '0%': { opacity: 0 },
                            '33%': { opacity: 1 },
                            '66%': { opacity: 1 },
                            '100%': { opacity: 0 },
                          },
                        }}
                      >
                        •
                      </Box>
                    </Box>
                  )}
                </Box>
              }
              iconPosition='start'
            />
            <Tab
              icon={<WorkIcon />}
              label={
                <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                  {`Close Matches ${fuzzyMatches?.jobs?.length ? `(${filterJobsByFavorites(fuzzyMatches.jobs).length})` : ''}`}
                  {isAiTyping && (
                    <Box component="span" sx={{ ml: 1, display: 'flex' }}>
                      <Box
                        component="span"
                        sx={{
                          animation: 'typing 1.5s infinite',
                          animationDelay: '0s',
                          opacity: 0,
                          '@keyframes typing': {
                            '0%': { opacity: 0 },
                            '33%': { opacity: 1 },
                            '66%': { opacity: 1 },
                            '100%': { opacity: 0 },
                          },
                        }}
                      >
                        •
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          animation: 'typing 1.5s infinite',
                          animationDelay: '0.5s',
                          opacity: 0,
                          '@keyframes typing': {
                            '0%': { opacity: 0 },
                            '33%': { opacity: 1 },
                            '66%': { opacity: 1 },
                            '100%': { opacity: 0 },
                          },
                        }}
                      >
                        •
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          animation: 'typing 1.5s infinite',
                          animationDelay: '1s',
                          opacity: 0,
                          '@keyframes typing': {
                            '0%': { opacity: 0 },
                            '33%': { opacity: 1 },
                            '66%': { opacity: 1 },
                            '100%': { opacity: 0 },
                          },
                        }}
                      >
                        •
                      </Box>
                    </Box>
                  )}
                </Box>
              }
              iconPosition='start'
            />
            <Tab
              icon={<BusinessIcon />}
              label={
                <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                  {`Hospital Matches ${hospitalMatches?.hospitals?.length ? `(${hospitalMatches.hospitals.length})` : ''}`}
                  {isAiTyping && (
                    <Box component="span" sx={{ ml: 1, display: 'flex' }}>
                      <Box
                        component="span"
                        sx={{
                          animation: 'typing 1.5s infinite',
                          animationDelay: '0s',
                          opacity: 0,
                          '@keyframes typing': {
                            '0%': { opacity: 0 },
                            '33%': { opacity: 1 },
                            '66%': { opacity: 1 },
                            '100%': { opacity: 0 },
                          },
                        }}
                      >
                        •
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          animation: 'typing 1.5s infinite',
                          animationDelay: '0.5s',
                          opacity: 0,
                          '@keyframes typing': {
                            '0%': { opacity: 0 },
                            '33%': { opacity: 1 },
                            '66%': { opacity: 1 },
                            '100%': { opacity: 0 },
                          },
                        }}
                      >
                        •
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          animation: 'typing 1.5s infinite',
                          animationDelay: '1s',
                          opacity: 0,
                          '@keyframes typing': {
                            '0%': { opacity: 0 },
                            '33%': { opacity: 1 },
                            '66%': { opacity: 1 },
                            '100%': { opacity: 0 },
                          },
                        }}
                      >
                        •
                      </Box>
                    </Box>
                  )}
                </Box>
              }
              iconPosition='start'
            />
          </Tabs>

          {/* Tab Content */}
          <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
            {/* Exact Matches Tab */}
            {activeTab === 0 && (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {filterJobsByFavorites(exactMatches?.jobs || []).length > 0 ? (
                  <>
                    {/* Results Header */}
                    <Box sx={{ p: 2, pb: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant='body1' fontWeight='medium' color='text.primary'>
                        Found {filterJobsByFavorites(exactMatches.jobs).length} jobs matching your criteria
                        {favoritesOnly && ` (favorites only)`}
                      </Typography>
                      {totalAIPages > 1 && (
                        <Pagination
                          count={totalAIPages}
                          page={currentPage}
                          onChange={handlePageChange}
                          color='primary'
                          size='small'
                        />
                      )}
                    </Box>

                    {/* Job Cards */}
                    <Stack 
                      spacing={1} 
                      sx={{ 
                        px: 2, 
                        flex: 1,
                        pb: 2,
                        minHeight: 0,
                        overflow: 'auto',
                        '&::-webkit-scrollbar': {
                          width: '6px',
                        },
                        '&::-webkit-scrollbar-track': {
                          backgroundColor: 'rgba(0,0,0,0.1)',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: theme.palette.secondary.main,
                          borderRadius: '3px',
                        }
                      }}
                    >
                      {currentAIJobs.map(job => (
                        <ListItemJob
                          key={job.id}
                          data={job}
                          isActive={selectedJob?.id === job.id}
                          onClick={handleJobClickById}
                          onFavoriteClick={onFavoriteClick}
                          isAiMode={true}
                        />
                      ))}
                    </Stack>

                    {/* Bottom Pagination */}
                    {totalAIPages > 1 && (
                      <Box
                        sx={{
                          p: 2,
                          borderTop: 1,
                          borderColor: 'divider',
                          display: 'flex',
                          justifyContent: 'center',
                          backgroundColor: theme.palette.grey[100],
                        }}
                      >
                        <Pagination
                          count={totalAIPages}
                          page={currentPage}
                          onChange={handlePageChange}
                          color='primary'
                          showFirstButton
                          showLastButton
                          size='small'
                        />
                      </Box>
                    )}
                  </>
                ) : (
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 4,
                      textAlign: 'center',
                    }}
                  >
                    {isAiTyping ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ 
                          position: 'relative', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          mb: 3
                        }}>
                          <CircularProgress 
                            size={60} 
                            sx={{ 
                              color: theme.palette.secondary.main,
                              '& .MuiCircularProgress-circle': {
                                strokeLinecap: 'round',
                              }
                            }} 
                          />
                          <AutoAwesomeIcon 
                            sx={{ 
                              position: 'absolute',
                              fontSize: '2rem', 
                              color: theme.palette.secondary.main,
                              animation: 'sparkle 1.5s ease-in-out infinite',
                              '@keyframes sparkle': {
                                '0%': { opacity: 0.7, transform: 'scale(1)' },
                                '50%': { opacity: 1, transform: 'scale(1.2)' },
                                '100%': { opacity: 0.7, transform: 'scale(1)' },
                              }
                            }} 
                          />
                        </Box>
                        <Typography 
                          variant='h6' 
                          sx={{ 
                            color: theme.palette.secondary.main, 
                            fontWeight: 'bold',
                            mb: 1,
                            animation: 'pulse 1.5s ease-in-out infinite',
                            '@keyframes pulse': {
                              '0%': { opacity: 0.7 },
                              '50%': { opacity: 1 },
                              '100%': { opacity: 0.7 },
                            }
                          }}
                        >
                          Searching for jobs...
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                          Our AI is analyzing opportunities for you...
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <AutoAwesomeIcon sx={{ fontSize: '3rem', color: theme.palette.secondary.main, mb: 2, opacity: 0.7 }} />
                        <Typography variant='h6' color='text.secondary' gutterBottom>
                          Ready to find your perfect job!
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                          {exactMatches?.jobs?.length > 0 && favoritesOnly
                            ? 'No favorite jobs found in exact matches.'
                            : 'Use the chat to search for jobs that match your criteria.'}
                        </Typography>
                      </>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {/* Close Matches Tab */}
            {activeTab === 1 && (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {fuzzyMatchesLoading ? (
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 4,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : filterJobsByFavorites(fuzzyMatches?.jobs || []).length > 0 ? (
                  <>
                    <Box sx={{ p: 2, pb: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant='body1' fontWeight='medium' color='text.primary'>
                        Found {filterJobsByFavorites(fuzzyMatches.jobs).length} alternative jobs that might interest you
                        {favoritesOnly && ` (favorites only)`}
                      </Typography>
                      {totalAIPages > 1 && (
                        <Pagination
                          count={totalAIPages}
                          page={currentPage}
                          onChange={handlePageChange}
                          color='primary'
                          size='small'
                        />
                      )}
                    </Box>

                    <Stack 
                      spacing={1} 
                      sx={{ 
                        px: 2, 
                        flex: 1,
                        pb: 2,
                        minHeight: 0,
                        overflow: 'auto',
                        '&::-webkit-scrollbar': {
                          width: '6px',
                        },
                        '&::-webkit-scrollbar-track': {
                          backgroundColor: 'rgba(0,0,0,0.1)',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: theme.palette.secondary.main,
                          borderRadius: '3px',
                        }
                      }}
                    >
                      {getCurrentJobs().map(job => (
                        <ListItemJob
                          key={job.id}
                          data={job}
                          isActive={selectedJob?.id === job.id}
                          onClick={handleJobClickById}
                          onFavoriteClick={onFavoriteClick}
                          isAiMode={true}
                        />
                      ))}
                    </Stack>
                    
                    {/* Bottom Pagination */}
                    {totalAIPages > 1 && (
                      <Box
                        sx={{
                          p: 2,
                          borderTop: 1,
                          borderColor: 'divider',
                          display: 'flex',
                          justifyContent: 'center',
                          backgroundColor: theme.palette.grey[100],
                        }}
                      >
                        <Pagination
                          count={totalAIPages}
                          page={currentPage}
                          onChange={handlePageChange}
                          color='primary'
                          showFirstButton
                          showLastButton
                          size='small'
                        />
                      </Box>
                    )}
                  </>
                ) : (
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 4,
                      textAlign: 'center',
                    }}
                  >
                    {isAiTyping ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ 
                          position: 'relative', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          mb: 3
                        }}>
                          <CircularProgress 
                            size={60} 
                            sx={{ 
                              color: theme.palette.secondary.main,
                              '& .MuiCircularProgress-circle': {
                                strokeLinecap: 'round',
                              }
                            }} 
                          />
                          <AutoAwesomeIcon 
                            sx={{ 
                              position: 'absolute',
                              fontSize: '2rem', 
                              color: theme.palette.secondary.main,
                              animation: 'sparkle 1.5s ease-in-out infinite',
                              '@keyframes sparkle': {
                                '0%': { opacity: 0.7, transform: 'scale(1)' },
                                '50%': { opacity: 1, transform: 'scale(1.2)' },
                                '100%': { opacity: 0.7, transform: 'scale(1)' },
                              }
                            }} 
                          />
                        </Box>
                        <Typography 
                          variant='h6' 
                          sx={{ 
                            color: theme.palette.secondary.main, 
                            fontWeight: 'bold',
                            mb: 1,
                            animation: 'pulse 1.5s ease-in-out infinite',
                            '@keyframes pulse': {
                              '0%': { opacity: 0.7 },
                              '50%': { opacity: 1 },
                              '100%': { opacity: 0.7 },
                            }
                          }}
                        >
                          Searching for alternative jobs...
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                          Finding jobs that closely match your criteria...
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant='body1' color='text.secondary'>
                        {fuzzyMatchesLoading
                          ? 'Searching for alternative jobs...'
                          : favoritesOnly
                          ? 'No favorite jobs found in alternative matches.'
                          : 'No alternative jobs found. Try searching for jobs first.'}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {/* Hospital Matches Tab */}
            {activeTab === 2 && (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {loading ? (
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 4,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : hospitalMatches?.hospitals?.length > 0 ? (
                  <>
                    <Box sx={{ p: 2, pb: 0 }}>
                      <Typography variant='body1' fontWeight='medium' color='text.primary'>
                        Found {hospitalMatches.hospitals.length} hospitals that match your criteria
                      </Typography>
                    </Box>

                    <Stack 
                      spacing={1} 
                      sx={{ 
                        px: 2, 
                        flex: 1,
                        pb: 2,
                        minHeight: 0,
                        overflow: 'auto',
                        '&::-webkit-scrollbar': {
                          width: '6px',
                        },
                        '&::-webkit-scrollbar-track': {
                          backgroundColor: 'rgba(0,0,0,0.1)',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: theme.palette.secondary.main,
                          borderRadius: '3px',
                        }
                      }}
                    >
                      {hospitalMatches.hospitals.map(hospital => (
                        <HospitalCard key={hospital.id} hospital={hospital} />
                      ))}
                    </Stack>
                  </>
                ) : (
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 4,
                      textAlign: 'center',
                    }}
                  >
                    {isAiTyping ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ 
                          position: 'relative', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          mb: 3
                        }}>
                          <CircularProgress 
                            size={60} 
                            sx={{ 
                              color: theme.palette.secondary.main,
                              '& .MuiCircularProgress-circle': {
                                strokeLinecap: 'round',
                              }
                            }} 
                          />
                          <BusinessIcon 
                            sx={{ 
                              position: 'absolute',
                              fontSize: '2rem', 
                              color: theme.palette.secondary.main,
                              animation: 'sparkle 1.5s ease-in-out infinite',
                              '@keyframes sparkle': {
                                '0%': { opacity: 0.7, transform: 'scale(1)' },
                                '50%': { opacity: 1, transform: 'scale(1.2)' },
                                '100%': { opacity: 0.7, transform: 'scale(1)' },
                              }
                            }} 
                          />
                        </Box>
                        <Typography 
                          variant='h6' 
                          sx={{ 
                            color: theme.palette.secondary.main, 
                            fontWeight: 'bold',
                            mb: 1,
                            animation: 'pulse 1.5s ease-in-out infinite',
                            '@keyframes pulse': {
                              '0%': { opacity: 0.7 },
                              '50%': { opacity: 1 },
                              '100%': { opacity: 0.7 },
                            }
                          }}
                        >
                          Searching for hospitals...
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                          Finding hospitals that match your search...
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant='body1' color='text.secondary'>
                        {loading
                          ? 'Searching for hospitals...'
                          : 'No hospitals found. Try searching for jobs first.'}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Grid>

      </Grid>

      {/* Job Modal */}
      <JobModal job={selectedJob} onClose={handleCloseModal} />
      
      {/* AI Chat Drawer - positioned outside Grid for proper FAB positioning */}
      <AIChatBot />
    </>
  )
}

export default AIJobInterface 